import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIconButton from '../components/DeleteIconButton';
import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid, TextField } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { DataAPIContext } from '../api';
import { CREATE, DELETE, UPDATE } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix, useIsWidthUp } from '../utils';
import validURL from '../utils/validators/url';

const DND_LINK = 'activity_link';

const useStyles = makeStyles((theme) => ({
    root: {},
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    dragHandle: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    dragPreview: {
        background: '#e6f2ff',
        border: '1px solid #b3d7ff',
        borderRadius: '5px',
    },
    arrowButton: {
        padding: '0.5rem',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            padding: '0.25rem',
        },
    },

}));

const LinkEditor = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [data, setData] = React.useState(props.data);
    const { hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const dndref = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const [dirty, setDirty] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [UrlErrorState, setUrlErrorState] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');


    const [, drag] = useDrag({
        type: DND_LINK,
        item: () => {
            return { id: data.id, idx: props.idx };
        },
        collect: (monitor, a) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        end: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });



    // eslint-disable-next-line no-unused-vars
    const [{ handlerId }, drop] = useDrop({
        accept: DND_LINK,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover: (item, monitor) => {
            if (!dndref.current) {
                return;
            }
            const dragIndex = item.idx;
            const hoverIndex = props.idx;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = dndref.current?.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            if (props.swapItems) {
                props.swapItems(dragIndex, hoverIndex);
                item.idx = hoverIndex;
            }
        },
        drop: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });
    drag(drop(dndref));

    const onSave = () => {
        if (props.documentsAndLinks.filter((link) => {
            if (link.id === data.id) {
                return false
            }
            return (link?.document?.link === data.document?.link || link.document?.link === checkAndAddHttpPrefix(data.document?.link))
        }).length > 0) {
            enqueueSnackbar('Duplicate link found, please check existing links', { variant: 'default' });
            return;
        }

        setSyncActive(true);
        const payload = { ...data, ...props.resourceMeta };
        const ACTION = payload.id ? UPDATE : CREATE;
        payload.document.link = checkAndAddHttpPrefix(payload.document.link);

        const params = { id: payload.id, data: payload };
        return dataAPI(ACTION, props.resource, params)
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('Link saved', { variant: 'success' });
                    setData(res.data);
                    setDirty(false);
                    props.onChange && props.onChange(res.data, props.data._idx);
                } else {
                    enqueueSnackbar('Error saving link: ' + res.statusCode, { variant: 'error', autoHideDuration: 5000 });
                }
            })
            .finally(() => setSyncActive(false));
    };

    const onLinkDeleted = () => {
        enqueueSnackbar('Link deleted', { variant: 'default' });
        setSyncActive(false);
        props.onDeleted && props.onDeleted(props.idx);
    };

    const onDelete = () => {
        if (data.id) {
            setSyncActive(true);
            dataAPI(DELETE, props.resource, { id: data.id }).then((res) => onLinkDeleted());
        } else {
            onLinkDeleted();
        }
    };

    const onChange = (ev) => {
        setData({ ...data, document: { ...data.document, [ev.target.name]: ev.target.value } });
        setDirty(true);
    };
    const onSwitchChange = (ev) => {
        setData({ ...data, document: { ...data.document, [ev.target.name]: ev.target.checked } });
        setDirty(true);
    };

    const onKeyPress = (ev) => {
        if (ev.keyCode === 13) {
            onSave();
        }
    };



    const handleURLOnBlur = () => {
        if (validURL(data.document.link)) {
            setUrlErrorState(false);
        } else {
            setUrlErrorState(true);
        }
    };
    const handleSwapItems = (idx, direction) => {
        if (direction === 'Down' && props.documentsAndLinks.length - 1 > idx) {
            props.swapItems(props.idx, props.idx + 1);
        }
        if (direction === 'Up' && idx > 0) {
            props.swapItems(props.idx, props.idx - 1);
        }
    };

    const handleCancelEdit = () => {
        setData({ ...props.data, document: { ...props.data.document } });
        setDirty(false);
        setUrlErrorState(false);
    };

    return (
        <>
            <FixedLinearProgress visibility={syncActive || props.data.uploading} />
            <Grid container direction="row" spacing={1}>
                <Grid item xs={12} container alignItems="center" spacing={2}>
                    <Grid item xs={3} sm={'auto'} container alignItems="center" width={isSmallScreen ? 85 : 100} justifyContent={'flex-start'}>
                        {hasPerm('activity', 'write') && (
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <button className={classes.arrowButton} style={{ color: props.idx === 0 && grey[400], cursor: props.idx === 0 && 'default' }} onClick={() => handleSwapItems(props.idx, 'Up')}>
                                    <ArrowUpwardIcon />
                                </button>
                                <button className={classes.arrowButton} style={{ color: props.idx === props.documentsAndLinks.length - 1 && grey[400], cursor: props.idx === props.documentsAndLinks.length - 1 && 'default' }} onClick={() => handleSwapItems(props.idx, 'Down')}>
                                    <ArrowDownwardIcon />
                                </button>
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={9} sm={'auto'}>
                        <TextField variant="filled" size="small" fullWidth name="link" label="Link" value={data.document.link} onChange={onChange} onKeyDown={onKeyPress} onBlur={handleURLOnBlur} error={UrlErrorState} helperText={UrlErrorState && 'Please enter a valid URL.'} />
                    </Grid>
                </Grid>
                <Grid item xs={12} container direction="row" spacing={1} alignItems="center">
                    <Grid item xs={3} sm={5} sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField variant="filled" size="small" fullWidth name="name" label="Display Name" value={data.document.name} onChange={onChange} onKeyDown={onKeyPress} />
                    </Grid>
                    <Grid item xs={'auto'} sm={'auto'} ml={2} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox color="primary" name="deep_link" onChange={onSwitchChange} onKeyDown={onKeyPress} checked={data.document.deep_link} />} label="Deep Link" />
                            {!isSmallScreen && <FormHelperText>Open in Safari vs. In-App Browser</FormHelperText>}
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormActionButtons
                            mode="edit"
                            onSubmit={onSave}
                            submitDisabled={!data.document.name || !data.document.link || (data.id && (!dirty || UrlErrorState || data.document.link === '' || syncActive))}
                            onCancel={handleCancelEdit}
                            cancelDisabled={!dirty && !UrlErrorState}
                            leadingActions={
                                <DeleteIconButton perm="activity.write" size="medium" tabIndex={-1} onClick={onDelete} />
                            }
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default LinkEditor;
